import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminOnly from "#/components/auth/AdminOnly";
import Typography from "#/components/shared/typography";
import { ThemedButtonGroup } from "#/components/shared/forms/ThemedButtonGroup";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { ThemedModal } from "#/components/shared/ThemedModal";
import MediaListPage from "#/features/media/MediaListPage";
import ArticleOutlinesList from "#/admin/article-outlines/components/ArticleOutlinesList";
import ArticlesList from "#/admin/articles/components/ArticlesList";
import ArticleReviewsLayout from "#/admin/article-reviews/components/ArticleReviewsLayout";
import MedicalConceptsLayout from "#/admin/medical-concepts/components/MedicalConceptsLayout";
import { UploadSourceDocumentForm } from "#/features/content/components/UploadSourceDocumentForm";
import { MediaUploadForm } from "#/features/media/components/MediaUploadForm";
import { MediaGenerateForm } from "#/features/media/components/MediaGenerateForm";
import { FileUploadContextWrapper } from "#/hooks/FileUploadContext";
import ArticleAiPolicyEditor from "#/admin/article-policy/components/ArticleAiPolicyEditor";

type ContentTab =
  | "media"
  | "outlines"
  | "medical-concepts"
  | "articles"
  | "reviews"
  | "ai-policy";

const contentTabs = [
  {
    value: "media",
    label: "Media Library",
    description: "Manage images and assets.",
  },
  {
    value: "articles",
    label: "Articles",
    description: "Browse the article catalog.",
  },
  {
    value: "outlines",
    label: "Article Outlines",
    description: "Create and review article outlines.",
  },
  {
    value: "medical-concepts",
    label: "Medical Concepts",
    description: "Manage concepts and trigger concept-driven generation.",
  },
  {
    value: "reviews",
    label: "Article Reviews",
    description: "Audit article review submissions.",
  },
  {
    value: "ai-policy",
    label: "AI Policy",
    description: "Edit article and media AI policy guidance.",
  },
];

function resolveTab(pathname: string, search: string): ContentTab {
  if (pathname === "/admin/content/outlines") return "outlines";
  if (pathname === "/admin/content/medical-concepts") return "medical-concepts";
  if (pathname === "/admin/content/articles") return "articles";
  if (pathname === "/admin/content/reviews") return "reviews";
  if (pathname === "/admin/content/ai-policy") return "ai-policy";
  if (pathname === "/admin/content/media") return "media";

  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  if (tab === "articles") return "articles";
  if (tab === "outlines") return "outlines";
  if (tab === "reviews") return "reviews";
  if (tab === "medical-concepts") return "medical-concepts";
  if (tab === "ai-policy") return "ai-policy";
  const hash = window.location.hash.replace("#", "");
  if (hash === "articles") return "articles";
  if (hash === "outlines") return "outlines";
  if (hash === "reviews") return "reviews";
  if (hash === "medical-concepts") return "medical-concepts";
  if (hash === "ai-policy") return "ai-policy";
  if (hash === "media") return "media";
  return "media";
}

function tabPath(tab: ContentTab) {
  return `/admin/content/${tab}`;
}

export default function AdminContentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = useMemo(
    () => resolveTab(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const [isUploadDocOpen, setIsUploadDocOpen] = useState(false);
  const [isUploadMediaOpen, setIsUploadMediaOpen] = useState(false);
  const [isGenerateMediaOpen, setIsGenerateMediaOpen] = useState(false);

  return (
    <AdminOnly>
      <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div className="flex flex-col gap-2">
          <Typography variant="h2" className="text-brown-900">
            Admin content
          </Typography>
          <Typography variant="body1" className="text-brown-600">
            Manage media assets, articles, and reviews in one place.
          </Typography>
          <div className="flex flex-wrap gap-3 text-sm text-brown-600">
            {contentTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => navigate(tabPath(tab.value as ContentTab))}
                className={`rounded-full border px-3 py-1 transition ${
                  activeTab === tab.value
                    ? "border-terracotta-300 bg-cream-100 text-terracotta-700"
                    : "border-cream-200 hover:border-terracotta-200"
                }`}
              >
                #{tab.value}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <ActionButton
              tone="secondary"
              onClick={() => setIsUploadDocOpen(true)}
            >
              Upload Document
            </ActionButton>
            <ActionButton
              tone="secondary"
              onClick={() => setIsUploadMediaOpen(true)}
            >
              Upload Media
            </ActionButton>
            <ActionButton
              tone="secondary"
              onClick={() => setIsGenerateMediaOpen(true)}
            >
              Generate Image
            </ActionButton>
          </div>
        </div>

        <ThemedButtonGroup
          options={contentTabs}
          value={activeTab}
          onChange={(value) => navigate(tabPath(value as ContentTab))}
        />

        {activeTab === "media" ? (
          <MediaListPage />
        ) : activeTab === "articles" ? (
          <ArticlesList />
        ) : activeTab === "outlines" ? (
          <ArticleOutlinesList />
        ) : activeTab === "medical-concepts" ? (
          <MedicalConceptsLayout />
        ) : activeTab === "reviews" ? (
          <ArticleReviewsLayout />
        ) : (
          <ArticleAiPolicyEditor />
        )}

        <ThemedModal
          isOpen={isUploadDocOpen}
          onClose={() => setIsUploadDocOpen(false)}
          title="Upload source document"
          description="Add a document to the knowledge base."
          tone="terracotta"
        >
          <FileUploadContextWrapper>
            <UploadSourceDocumentForm
              compact
              onComplete={() => setIsUploadDocOpen(false)}
            />
          </FileUploadContextWrapper>
        </ThemedModal>

        <ThemedModal
          isOpen={isUploadMediaOpen}
          onClose={() => setIsUploadMediaOpen(false)}
          title="Upload media"
          description="Upload a single image to the media library."
          tone="terracotta"
        >
          <MediaUploadForm
            compact
            onComplete={() => setIsUploadMediaOpen(false)}
          />
        </ThemedModal>

        <ThemedModal
          isOpen={isGenerateMediaOpen}
          onClose={() => setIsGenerateMediaOpen(false)}
          title="Generate media"
          description="Create a new image from an existing asset or text prompt."
          tone="terracotta"
        >
          <MediaGenerateForm
            onComplete={() => setIsGenerateMediaOpen(false)}
          />
        </ThemedModal>
      </div>
    </AdminOnly>
  );
}
